import { createAsyncThunk } from '@reduxjs/toolkit'
import { clearState as clearAiPlayerChatState } from '@/redux/slices/aiPlayerChatSlice'
import {
  createNewRoom,
  deleteRoomAPI,
  fetchDashboardKPIsAPI,
  fetchAllRooms,
  getRoomDetailsAPI,
  resendInviteAPI,
  sendInviteAPI,
  removePlayerAPI,
  bulkRemoveStudentsAPI,
  updateRoomDetailsAPI,
  resetRoomAPI,
  getRoomMembersAPI,
  duplicateRoomAPI,
  getCoProfessorsAPI,
  leaveProfessorGameAPI,
  type CoProfessor,
} from '@/api/room'

interface SendInviteParams {
  roomId: string
  emails: string[]
  playerRoles?: Record<string, string>
  userRole: UserRole
  sendEmail?: boolean
  teamAssignments?: Record<string, number>
  requirePasswordConfiguration?: boolean
}

interface RemovePlayerParams {
  roomId: string
  playerId: string
}

import { User } from '../types/user'
import { UserRole } from '@/constants/room'
import {
  DashboardKPIs,
  EventMediaItem,
  LLMChoice,
  ResourceEnforcementMode,
} from '../types/room'
import { TimerMode } from '@/components/settings/timer-configuration/types'

export const createRoom = createAsyncThunk(
  'room/createRoom',
  async (
    params: {
      user: User
      roomName: string
      timerMode: TimerMode
      /** Create a standalone Charter Class instead of a game room. */
      isCharterOnly?: boolean
    },
    thunkAPI
  ) => {
    try {
      const response = await createNewRoom(
        params.user,
        params.roomName,
        params.timerMode,
        params.isCharterOnly ?? false
      )
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)

export const duplicateRoom = createAsyncThunk(
  'room/duplicateRoom',
  async (
    { roomId, withAssignments }: { roomId: string; withAssignments: boolean },
    thunkAPI
  ) => {
    try {
      const response = await duplicateRoomAPI(roomId, withAssignments)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)

export const fetchRooms = createAsyncThunk(
  'room/fetchRooms',
  async (
    params: { sortBy?: string; searchQuery?: string } | string | undefined,
    thunkAPI
  ) => {
    try {
      // Handle both old string format (for backward compatibility) and new object format
      let sortBy: string | undefined
      let searchQuery: string | undefined

      if (typeof params === 'string') {
        sortBy = params
      } else if (params && typeof params === 'object') {
        sortBy = params.sortBy
        searchQuery = params.searchQuery
      }

      const response = await fetchAllRooms(sortBy, searchQuery)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)

/**
 * Charter Classes list, kept separate from `fetchRooms` so the two dashboards
 * write to different slices of state instead of overwriting each other.
 */
export const fetchCharterClasses = createAsyncThunk(
  'room/fetchCharterClasses',
  async (
    params: { sortBy?: string; searchQuery?: string } | undefined,
    thunkAPI
  ) => {
    try {
      const response = await fetchAllRooms(
        params?.sortBy,
        params?.searchQuery,
        'charter'
      )
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)

export const fetchDashboardKPIs = createAsyncThunk<
  DashboardKPIs,
  void,
  { rejectValue: string }
>('room/fetchDashboardKPIs', async (_, thunkAPI) => {
  try {
    const response = await fetchDashboardKPIsAPI()
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue((error as Error).message)
  }
})

export const deleteRoom = createAsyncThunk(
  'room/deleteRoom',
  async (roomId: string, thunkAPI) => {
    try {
      await deleteRoomAPI(roomId)
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)

export const getRoomDetails = createAsyncThunk(
  'room/getRoomDetails',
  async (roomId: string, thunkAPI) => {
    try {
      const response = await getRoomDetailsAPI(roomId)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)

export const sendInvite = createAsyncThunk(
  'room/sendInvite',
  async (
    {
      roomId,
      emails,
      userRole,
      sendEmail,
      teamAssignments,
      requirePasswordConfiguration,
    }: SendInviteParams,
    thunkAPI
  ) => {
    try {
      const response = await sendInviteAPI(
        roomId,
        emails,
        userRole,
        sendEmail,
        teamAssignments,
        requirePasswordConfiguration
      )
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)

export const resendInvite = createAsyncThunk(
  'room/resendInvite',
  async (
    {
      roomId,
      emails,
      userRole,
      playerRoles,
      customMessage,
    }: SendInviteParams & { customMessage?: string },
    thunkAPI
  ) => {
    try {
      const response = await resendInviteAPI(
        roomId,
        emails,
        userRole,
        playerRoles,
        customMessage
      )
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)

export const getRoomMembers = createAsyncThunk(
  'room/getRoomMembers',
  async (roomId: string, thunkAPI) => {
    try {
      const response = await getRoomMembersAPI(roomId)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)

export const removePlayer = createAsyncThunk(
  'room/removePlayer',
  async ({ roomId, playerId }: RemovePlayerParams, thunkAPI) => {
    try {
      const response = await removePlayerAPI(roomId, playerId)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)

export const bulkRemoveStudents = createAsyncThunk(
  'room/bulkRemoveStudents',
  async (roomId: string, thunkAPI) => {
    try {
      const response = await bulkRemoveStudentsAPI(roomId)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)

export const fetchCoProfessors = createAsyncThunk<
  CoProfessor[],
  string,
  { rejectValue: string }
>('room/fetchCoProfessors', async (roomId, thunkAPI) => {
  try {
    return await getCoProfessorsAPI(roomId)
  } catch (error) {
    return thunkAPI.rejectWithValue((error as Error).message)
  }
})

export const leaveProfessorGame = createAsyncThunk<
  string,
  { roomId: string; newOwnerId?: number },
  { rejectValue: string }
>('room/leaveProfessorGame', async ({ roomId, newOwnerId }, thunkAPI) => {
  try {
    await leaveProfessorGameAPI(roomId, newOwnerId)
    return roomId
  } catch (error) {
    return thunkAPI.rejectWithValue((error as Error).message)
  }
})

export const updateRoomDetails = createAsyncThunk(
  'room/updateRoomDetails',
  async (
    {
      roomId,
      scenario_title,
      events,
      status,
      start_time,
      ends_in,
      roomname,
      gameroom_llm,
      auto_approve,
      enable_chat_history_summarization,
      is_locked,
      is_simulating,
      resource_limits_enabled,
      resource_enforcement_mode,
      default_currency_amount,
      default_message_limit,
      default_token_limit,
      openai_api_key,
      claude_api_key,
    }: {
      roomId: string
      scenario_title?: string
      events?: {
        id?: number
        title: string
        text: string
        event_datetime: Date
        is_released: boolean
        media?: EventMediaItem[]
        use_ttq?: boolean
        affects_all_players?: boolean
        assigned_player_ids?: number[]
        affected_team_ids?: number[]
      }[]
      status?: number
      start_time?: Date
      ends_in?: number
      roomname?: string
      gameroom_llm?: LLMChoice
      auto_approve?: boolean
      enable_chat_history_summarization?: boolean
      is_locked?: boolean
      is_simulating?: boolean
      resource_limits_enabled?: boolean
      resource_enforcement_mode?: ResourceEnforcementMode
      default_currency_amount?: number | null
      default_message_limit?: number | null
      default_token_limit?: number | null
      openai_api_key?: string | null
      claude_api_key?: string | null
    },
    thunkAPI
  ) => {
    try {
      const payload: {
        scenario_title?: string
        events?: {
          id?: number
          title: string
          text: string
          event_datetime: Date
          is_released: boolean
          media?: EventMediaItem[]
          affects_all_players?: boolean
          assigned_player_ids?: number[]
          affected_team_ids?: number[]
        }[]
        status?: number
        start_time?: string
        ends_in?: number
        roomname?: string
        gameroom_llm?: string
        auto_approve?: boolean
        enable_chat_history_summarization?: boolean
        is_locked?: boolean
        is_simulating?: boolean
        resource_limits_enabled?: boolean
        resource_enforcement_mode?: ResourceEnforcementMode
        default_currency_amount?: number | null
        default_message_limit?: number | null
        default_token_limit?: number | null
        openai_api_key?: string | null
        claude_api_key?: string | null
      } = {}

      if (scenario_title !== undefined) {
        payload.scenario_title = scenario_title
      }

      if (events !== undefined) {
        payload.events = events.map(
          ({
            id,
            title,
            text,
            event_datetime,
            is_released,
            media,
            use_ttq,
            affects_all_players,
            assigned_player_ids,
            affected_team_ids,
          }) => ({
            id,
            title,
            text,
            event_datetime,
            is_released,
            media_ids: (media ?? []).map((m) => m.id),
            use_ttq: use_ttq ?? false,
            // Only the scenario form owns the audience. Defaulting these
            // here would reset it to everyone on every release.
            ...(affects_all_players !== undefined && { affects_all_players }),
            ...(assigned_player_ids !== undefined && { assigned_player_ids }),
            ...(affected_team_ids !== undefined && { affected_team_ids }),
          })
        )
      }

      if (status !== undefined) {
        payload.status = status
      }

      if (roomname !== undefined) {
        payload.roomname = roomname
      }

      if (start_time) {
        payload.start_time = start_time.toISOString()
      } else if (ends_in !== undefined) {
        payload.ends_in = ends_in
      }

      if (gameroom_llm !== undefined) {
        payload.gameroom_llm = gameroom_llm
      }

      if (auto_approve !== undefined) {
        payload.auto_approve = auto_approve
      }

      if (enable_chat_history_summarization !== undefined) {
        payload.enable_chat_history_summarization =
          enable_chat_history_summarization
      }

      if (is_locked !== undefined) {
        payload.is_locked = is_locked
      }

      if (is_simulating !== undefined) {
        payload.is_simulating = is_simulating
      }

      if (resource_limits_enabled !== undefined) {
        payload.resource_limits_enabled = resource_limits_enabled
      }

      if (resource_enforcement_mode !== undefined) {
        payload.resource_enforcement_mode = resource_enforcement_mode
      }

      if (default_currency_amount !== undefined) {
        payload.default_currency_amount = default_currency_amount
      }

      if (default_message_limit !== undefined) {
        payload.default_message_limit = default_message_limit
      }

      if (default_token_limit !== undefined) {
        payload.default_token_limit = default_token_limit
      }

      if (openai_api_key !== undefined) {
        payload.openai_api_key = openai_api_key
      }
      if (claude_api_key !== undefined) {
        payload.claude_api_key = claude_api_key
      }

      const response = await updateRoomDetailsAPI(roomId, payload)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message)
    }
  }
)
export const resetGame = createAsyncThunk(
  'room/resetGame',
  async (roomId: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await resetRoomAPI(roomId)
      dispatch(clearAiPlayerChatState())
      return response.data
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to reset game'
      )
    }
  }
)
